import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { ZONES } from "@/data/site";

export function ExploreMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      clearTimeout(timer.current);
    };
  }, []);

  const enter = () => { clearTimeout(timer.current); setOpen(true); };
  const leave = () => { timer.current = setTimeout(() => setOpen(false), 160); };

  return (
    <div ref={ref} className="relative" onMouseEnter={enter} onMouseLeave={leave}>
      <button onClick={() => setOpen((o) => !o)} data-testid="nav-explore-toggle" aria-expanded={open}
        className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-ink/80 hover:text-ink transition-colors">
        Explore <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }} data-testid="explore-menu"
            className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[760px]">
            <div className="rounded-[28px] bg-paper border border-ink/10 shadow-[0_30px_80px_-30px_rgba(21,17,14,0.45)] p-6">
              <div className="flex items-end justify-between mb-4 px-2">
                <p className="eyebrow text-terra">Explore by region</p>
                <Link to="/trips" onClick={() => setOpen(false)} className="text-sm font-semibold hover:text-terra">All trips →</Link>
              </div>
              <div className="grid grid-cols-3 gap-1">
                {ZONES.map((z) => (
                  <Link key={z.slug} to={`/trips?zone=${z.slug}`} onClick={() => setOpen(false)} data-testid={`explore-zone-${z.slug}`}
                    className="group rounded-2xl px-3 py-2.5 hover:bg-cream transition-colors">
                    <span className="flex items-center justify-between font-display font-bold text-ink">
                      {z.name}
                      <ArrowUpRight className="h-4 w-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform] text-terra" />
                    </span>
                    <span className="block text-xs text-stone-600 mt-0.5">{z.hint}</span>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function MobileExplore({ onNavigate }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} data-testid="mobile-explore-toggle"
        className="w-full flex items-center justify-between py-2 font-display text-3xl font-bold tracking-tight">
        Explore <ChevronDown className={`h-6 w-6 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 pb-3" data-testid="mobile-explore-list">
          {ZONES.map((z) => (
            <Link key={z.slug} to={`/trips?zone=${z.slug}`} onClick={onNavigate} data-testid={`mobile-zone-${z.slug}`}
              className="py-1.5 text-ink/80 hover:text-terra text-sm">{z.name}</Link>
          ))}
        </div>
      )}
    </div>
  );
}
